import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../../../components/layout/MainLayout";
import Breadcrumbs from "../../../../components/common/Breadcrumbs";
import RelocationForm from "./RelocationForm";
import { useDispatch } from "react-redux";
import { createRelocationForm } from "../../../../store/slices/relocationFormsSlice";
import { fetchPatient } from "../../../../store/slices/patientsSlice";
import { snakeCase } from "lodash"; // Install lodash if not already

const AddRelocation = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};
    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        caseId: caseId || null,
        patientId: patientId || null,
        standardName: "",
        issueDate: "",
        reviewDate: "",
        documentNumber: "",
        revisionNumber: "",
        departmentName: "",
        patientName: "",
        patientGender: "",
        patientDob: "",
        patientContact: "",
        currentMedication: "",
        quetiapine: "", quetiapineDose: "", quetiapineType: "",
        secondQuetiapineDose: "", // Added fields if needed
        serenace: "", serenaceDose: "", serenaceType: "",
        avil: "", avilDose: "", avilType: "",
        clopixol: "", clopixolDose: "", clopixolType: "",
        dormicum: "", dormicumDose: "", dormicumType: "",
        vallium: "", valliumDose: "", valliumType: "",
        procyclidine: "", procyclidineDose: "",
        doctorRemarks: "",
        doctorName: "",
        doctorDesignation: "",
        doctorPlace: "",
        rnQuetiapine: "", rnQuetiapineDose: "",
        rnSerenace: "", rnSerenaceDose: "", rnSerenaceType: "",
        rnAvil: "", rnAvilDose: "", rnAvilType: "",
        rnClopixol: "", rnClopixolDose: "", rnClopixolType: "",
        rnDormicum: "", rnDormicumDose: "", rnDormicumType: "",
        rnVallium: "", rnValliumDose: "", rnValliumType: "",
        rnProcyclidine: "", rnProcyclidineDose: "",
        rnRemarks: "",
        rnName: "",
        rnDesignation: "",
        rnPlace: ""
    });

    useEffect(() => {
        if (patientId) {
            dispatch(fetchPatient(patientId))
                .unwrap()
                .then((patient) => {
                    if (patient) {
                        setFormData((prev) => ({
                            ...prev,
                            patientName: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
                            patientDob: patient.dob ? patient.dob.split('T')[0] : "",
                            patientGender: patient.gender || "",
                            patientContact: patient.phone || "",
                        }));
                    }
                })
                .catch((err) => console.error("Failed to fetch patient details:", err));
        }
    }, [dispatch, patientId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Convert camelCase keys to snake_case
    const toSnakeCase = (obj) => {
        const result = {};
        for (let key in obj) {
            result[snakeCase(key)] = obj[key];
        }
        return result;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Create a copy of formData to modify
            const formDataToSend = { ...formData };

            // List of medication fields and their corresponding dose/type fields
            const medications = [
                { parent: 'quetiapine', dose: 'quetiapineDose', type: 'quetiapineType' },
                { parent: 'serenace', dose: 'serenaceDose', type: 'serenaceType' },
                { parent: 'avil', dose: 'avilDose', type: 'avilType' },
                { parent: 'clopixol', dose: 'clopixolDose', type: 'clopixolType' },
                { parent: 'dormicum', dose: 'dormicumDose', type: 'dormicumType' },
                { parent: 'vallium', dose: 'valliumDose', type: 'valliumType' },
                { parent: 'procyclidine', dose: 'procyclidineDose', type: 'procyclidineType' },
                { parent: 'rnQuetiapine', dose: 'rnQuetiapineDose', type: 'rnQuetiapineType' },
                { parent: 'rnSerenace', dose: 'rnSerenaceDose', type: 'rnSerenaceType' },
                { parent: 'rnAvil', dose: 'rnAvilDose', type: 'rnAvilType' },
                { parent: 'rnClopixol', dose: 'rnClopixolDose', type: 'rnClopixolType' },
                { parent: 'rnDormicum', dose: 'rnDormicumDose', type: 'rnDormicumType' },
                { parent: 'rnVallium', dose: 'rnValliumDose', type: 'rnValliumType' },
                { parent: 'rnProcyclidine', dose: 'rnProcyclidineDose', type: 'rnProcyclidineType' },
            ];

            // Process each medication
            medications.forEach(med => {
                // If parent is not "Yes", set dose and type to null
                if (formDataToSend[med.parent] !== "Yes") {
                    formDataToSend[med.dose] = null;
                    formDataToSend[med.type] = null;
                }
            });

            // Convert to snake_case for backend
            const payload = toSnakeCase(formDataToSend);

            await dispatch(createRelocationForm(payload)).unwrap();

            Swal.fire({
                title: "Saved!",
                text: "Relocation Form completed.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });

            navigate(`/cases/${caseId}/profile`, { state: { patientId, caseId } });
        } catch (err) {
            Swal.fire({
                title: "Error!",
                text: err?.message || "Failed to save relocation form.",
                icon: "error",
            });
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <RelocationForm formData={formData} handleChange={handleChange} />
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button type="submit" className="btn btn-primary px-4">
                                        <i className="ri-save-2-line me-1"></i> Save
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddRelocation;
